/**
 * MKS-314 follow-up — a money aggregate shown in the instance reporting
 * currency, with the rate source and basis one hover away.
 *
 * The ticket's requirement is that "the rate source and date show wherever a
 * converted figure appears". A shared component rather than a per-surface
 * `title=` means the wording is written once: the To Be Invoiced queue, the
 * revenue report and the project card cannot drift into explaining the same
 * ECB monthly average three different ways.
 *
 * A native `title` rather than the CSS tooltip `MoneyMetricLabel` uses, on
 * purpose: these render inside table cells and KPI tiles by the hundred, the
 * ticket says "a tooltip is enough", and a positioned overlay inside a
 * scrolling money column clips against the cell it belongs to.
 *
 * WITH NO CONVERSION IN FORCE THIS IS A PLAIN SPAN. No title, no underline,
 * no attribution — identical to what `formatCurrency` renders today, which is
 * what keeps the release flag's off branch unchanged. It is also what the
 * ticket means by "`source` is null when nothing was converted — the tooltip
 * must not claim a source".
 */
import { reportingMoney, reportingMoneySum } from '../../utils/reportingMoney';

interface CommonProps {
  /** Matches `formatCurrency`'s own flag so a call site can swap 1:1. */
  showDecimals?: boolean;
  /**
   * Exact fraction-digit control, matching `formatMoney`'s third argument. A
   * call site swapping off `formatMoney` must pass what it passed there, or
   * the release flag's off branch stops rendering byte-identically.
   */
  numberOptions?: Intl.NumberFormatOptions;
  className?: string;
}

interface SingleValueProps extends CommonProps {
  /** The CZK ledger figure. */
  value: number;
  /** 'YYYY-MM' the figure belongs to. Omitted → the newest month in force. */
  month?: string;
  parts?: never;
}

interface SummedProps extends CommonProps {
  /**
   * The CZK ledger figure broken down by month. Each month converts at its own
   * rate and the products are summed — never one rate on one total.
   */
  parts: ReadonlyArray<{ month: string; amount: number }>;
  value?: never;
  month?: never;
}

export type ReportingMoneyProps = SingleValueProps | SummedProps;

export function ReportingMoney({
  showDecimals = true,
  numberOptions,
  className = '',
  ...rest
}: ReportingMoneyProps) {
  const result =
    rest.parts !== undefined
      ? reportingMoneySum(rest.parts, { showDecimals, numberOptions })
      : reportingMoney(rest.value ?? 0, { month: rest.month, showDecimals, numberOptions });

  // The dotted underline is the affordance that says "there is something to
  // hover here"; without a tooltip there is nothing to point at, so it is not
  // drawn. `cursor-help` only ever pairs with a real title.
  const hint = result.title ? 'cursor-help underline decoration-dotted underline-offset-2' : '';

  return (
    <span
      className={`${hint} ${className}`.trim()}
      title={result.title}
      data-fx-unavailable={result.fxUnavailable ? 'true' : undefined}
    >
      {result.text}
      {result.fxUnavailable ? (
        <span className="ml-1 text-amber-600" aria-label="Exchange rate unavailable">
          *
        </span>
      ) : null}
    </span>
  );
}
